
import { useState, useRef, useCallback, useEffect } from 'react';
import { localizeTimeForSpeech } from '../utils/numberLocalization';
import type { SupportedLanguage } from '../utils/numberLocalization';

interface UseTimerProps {
  initialTime?: number;
  onComplete?: () => void;
  t?: (key: string, params?: any) => string;
  language?: string;
  soundEnabled?: boolean;
}

export function useTimer({ initialTime = 0, onComplete, t, language, soundEnabled }: UseTimerProps = {}) {
  const [time, setTime] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const announcedMilestonesRef = useRef<Set<number>>(new Set());
  const lastTimeRef = useRef<number>(0);
  const soundEnabledRef = useRef(soundEnabled);
  
  // 更新soundEnabled的ref值
  soundEnabledRef.current = soundEnabled;

  // 当soundEnabled变为false时，立即取消当前的语音播报
  useEffect(() => {
    if (!soundEnabled && 'speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  }, [soundEnabled]);

  const announceRemainingTime = useCallback((remainingMs: number) => {
    if ('speechSynthesis' in window && t && soundEnabledRef.current) {
      // 立即取消之前的语音
      speechSynthesis.cancel();
      
      const totalSeconds = Math.round(remainingMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      
      // 本地化数字用于语音播报
      const localizedTime = localizeTimeForSpeech(
        minutes, 
        seconds, 
        (language || 'en') as SupportedLanguage
      );
      
      let timeText = '';
      if (minutes > 0) {
        if (seconds > 0) {
          timeText = t('common:message.timeRemaining', { 
            minutes: localizedTime.minutes, 
            seconds: localizedTime.seconds 
          });
        } else {
          timeText = t('common:message.minutesRemaining', { 
            minutes: localizedTime.minutes 
          });
        }
      } else {
        timeText = t('common:message.secondsRemaining', { 
          seconds: localizedTime.seconds 
        });
      }
      
      // 调试信息
      console.log('翻译调试:', {
        key: 'common:message.secondsRemaining',
        params: { seconds: localizedTime.seconds },
        result: timeText,
        language: language
      });
      
      console.log('语音播报:', timeText, '剩余毫秒:', remainingMs, '准确秒数:', totalSeconds, '原始数字:', {minutes, seconds}, '本地化数字:', localizedTime);
      
      // 直接创建并播放语音
      const utterance = new SpeechSynthesisUtterance(timeText);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      // 根据当前语言设置语音语言
      utterance.lang = language === 'zh' ? 'zh-CN' : 'en-US';
      
      // 添加语音事件监听
      utterance.onstart = () => console.log('语音开始播放');
      utterance.onend = () => console.log('语音播放结束');
      utterance.onerror = (e) => console.error('语音播放错误:', e);
      
      speechSynthesis.speak(utterance);
    }
  }, [t, language]); // 保留language依赖，因为它用于本地化数字

  const getMilestones = useCallback((startMs: number) => {
    const startMinutes = startMs / (60 * 1000);
    const milestones: number[] = [];
    
    if (startMinutes >= 15) {
      // 大于等于15分钟：10分钟、3分钟、1分钟
      milestones.push(10 * 60 * 1000, 3 * 60 * 1000, 1 * 60 * 1000);
    } else if (startMinutes >= 5) {
      // 5-15分钟：3分钟、1分钟
      milestones.push(3 * 60 * 1000, 1 * 60 * 1000);
    } else if (startMinutes >= 2) {
      // 2-5分钟：1分钟、30秒
      milestones.push(1 * 60 * 1000, 30 * 1000);
    } else if (startMinutes >= 1) {
      // 1-2分钟：30秒、10秒
      milestones.push(30 * 1000, 10 * 1000);
    } else if (startMinutes >= 0.2) {
      // 30秒-1分钟：10秒
      milestones.push(10 * 1000);
    }
    
    console.log(`定时器时长: ${startMinutes}分钟, 播报里程碑:`, milestones.map(m => m/1000 + '秒'));
    return milestones;
  }, []);

  const start = useCallback(() => {
    if (!isRunning && time > 0) {
      setIsRunning(true);
      startTimeRef.current = time;
      announcedMilestonesRef.current.clear();
      
      console.log('定时器开始，总时长:', time/1000, '秒');
      
      intervalRef.current = setInterval(() => {
        setTime((prevTime) => {
          const newTime = prevTime - 10;
          
          if (newTime <= 0) {
            setIsRunning(false);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            // 延迟调用onComplete，确保状态更新完成
            setTimeout(() => {
              if (onComplete) {
                onComplete();
              }
            }, 100);
            return 0;
          }
          
          // 检查语音播报里程碑 - 使用精确匹配
          const milestones = getMilestones(startTimeRef.current);
          
          for (const milestone of milestones) {
            // 检查当前时间是否刚好经过里程碑时间
            // 当 newTime <= milestone 且 lastTime > milestone 时触发
            const lastTime = lastTimeRef.current;
            
            if (newTime <= milestone && lastTime > milestone && !announcedMilestonesRef.current.has(milestone)) {
              console.log(`里程碑播报: ${milestone/1000}秒`);
              announcedMilestonesRef.current.add(milestone);
              // 只在设置的里程碑时间进行播报
              announceRemainingTime(milestone);
              break;
            }
          }
          
          // 更新上次时间记录
          lastTimeRef.current = newTime;
          
          return newTime;
        });
      }, 10);
    }
  }, [isRunning, time, onComplete, getMilestones, announceRemainingTime]);

  const pause = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTime(initialTime);
    startTimeRef.current = 0;
    announcedMilestonesRef.current.clear();
    
    // 取消所有语音
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  }, [initialTime]);

  const setTimerTime = useCallback((newTime: number) => {
    setTime(newTime);
    // 当设置新时间时，清除已播报的里程碑
    announcedMilestonesRef.current.clear();
  }, []);

  const canStart = time > 0 && !isRunning;

  return {
    time,
    isRunning,
    canStart,
    start,
    pause,
    reset,
    setTime: setTimerTime
  };
}
